import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, orderBy, query, doc, getDoc, addDoc, deleteDoc, updateDoc, where } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { esCasaCaseta } from '../utils/morosoUtils';
import './MigracionMesesPendientes.css';

const BLOQUE_SIZE = 45;
const PAUSA_MS = 6000;

const MESES_OPCIONES = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
];

const getNombreMes = (numMes) => {
    const mes = MESES_OPCIONES.find(m => m.value === Number(numMes));
    return mes ? mes.label : `Mes ${numMes}`;
};

const formatPeriodoHistorico = (item) => {
    const anioInicio = item.anioInicio || item.añoInicio;
    const anioFin = item.anioFin || item.añoFin;
    const mesInicio = item.mesInicio;
    const mesFin = item.mesFin;

    if (mesInicio && mesFin) {
        return `${getNombreMes(mesInicio)} ${anioInicio} - ${getNombreMes(mesFin)} ${anioFin}`;
    }
    if (mesInicio && !mesFin) {
        return `${getNombreMes(mesInicio)} ${anioInicio} - Diciembre ${anioFin}`;
    }
    return `${anioInicio} - ${anioFin}`;
};

const MigracionMesesPendientes = () => {
    const { userData } = useAuth();
    const { showNotification } = useNotification();
    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd || null;

    const [fase, setFase] = useState('INICIO'); // INICIO | PROCESANDO | PAUSADO_LOTE | COMPLETADO
    const [casas, setCasas] = useState([]);
    const [indiceActual, setIndiceActual] = useState(0);
    const [resultados, setResultados] = useState({
        total: 0, migradas: 0, saltadas: 0, errores: 0, detalles: []
    });
    const [showDetails, setShowDetails] = useState(false);
    const [stopRequested, setStopRequested] = useState(false);

    // Búsqueda individual
    const [casaBusqueda, setCasaBusqueda] = useState('');
    const [procesandoIndividual, setProcesandoIndividual] = useState(false);

    // Modo de operación: HISTORICO (regularización de meses) o REVERSION (corrección cargos dobles junio 2026)
    const [opTipo, setOpTipo] = useState('HISTORICO'); 

    // Histórico de Cuotas y sub-operaciones
    const [regHistoricoList, setRegHistoricoList] = useState([]);
    const [regSubdivisionConfig, setRegSubdivisionConfig] = useState(null);
    const [subOp, setSubOp] = useState('MIGRACION'); // MIGRACION | RECALCULO
    const [mesCambioCuota, setMesCambioCuota] = useState(1); // 1-12 (Enero por defecto)
    const [anioCambioCuota, setAnioCambioCuota] = useState(new Date().getFullYear());
    const [guardandoVigencia, setGuardandoVigencia] = useState(false);

    // Formulario unificado de Vigencia de Cuota de Mantenimiento
    const [vigenciaForm, setVigenciaForm] = useState({
        cuota: '',
        mesInicio: 1,
        anioInicio: new Date().getFullYear(),
        mesFin: 12,
        anioFin: new Date().getFullYear()
    });

    const cambiarPestana = (tipo) => {
        setOpTipo(tipo);
        setFase('INICIO');
        setIndiceActual(0);
        setResultados({
            total: casas.length, migradas: 0, saltadas: 0, errores: 0, detalles: []
        });
        setStopRequested(false);
        setCasaBusqueda('');
    };

    // Cálculos dinámicos de fechas
    const now = new Date();
    const añoActual = now.getFullYear();
    const mesActual = now.getMonth() + 1;

    // Generar lista de meses del año en curso hasta el actual
    const mesesARegularizar = [];
    const nombresMeses = {}; // { '2026-04': 'Abr' }
    for (let i = 0; i < mesActual; i++) {
        const d = new Date(añoActual, i);
        const id = `${añoActual}-${String(i + 1).padStart(2, '0')}`;
        const nombreStr = new Intl.DateTimeFormat('es-MX', { month: 'short' }).format(d);
        const nombre = nombreStr.charAt(0).toUpperCase() + nombreStr.slice(1).replace('.', '');
        mesesARegularizar.push(id);
        nombresMeses[id] = nombre;
    }

    // Detección de casetas que registren saldos o adeudos residuales
    const casetasConSaldo = casas.filter(c => esCasaCaseta(c) && ((Number(c.adeudo) || 0) > 0 || (Number(c.cuotasExtras) || 0) > 0 || (Number(c.mesesAdeudo) || 0) > 0));

    // Cargar casas, configuración e histórico de cuotas al inicio
    useEffect(() => {
        const fetchData = async () => {
            if (!fraccionamientoId) return;
            try {
                // casas
                const q = query(collection(db, 'fraccionamientos', fraccionamientoId, 'casas'), orderBy('numero'));
                const snap = await getDocs(q);
                const casasList = snap.docs.map(doc => ({ id: doc.id, numero: doc.data().numero, ...doc.data() }));
                setCasas(casasList);
                setResultados(prev => ({ ...prev, total: casasList.length }));

                // histórico cuotas ordenado por periodo descendente
                const histRef = collection(db, 'fraccionamientos', fraccionamientoId, 'historicoCuotas');
                const histSnap = await getDocs(histRef);
                const histList = histSnap.docs.map(d => ({ id: d.id, ...d.data() }))
                    .sort((a, b) => {
                        const scoreA = (b.anioInicio || b.añoInicio || 0) * 12 + (b.mesInicio || 1);
                        const scoreB = (a.anioInicio || a.añoInicio || 0) * 12 + (a.mesInicio || 1);
                        return scoreA - scoreB;
                    });
                setRegHistoricoList(histList);

                // Configuración de fraccionamiento para cuota vigente
                const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
                const fraccSnap = await getDoc(fraccRef);
                if (fraccSnap.exists()) {
                    const cfgFin = fraccSnap.data().configuracionFinanciera || {};
                    setRegSubdivisionConfig(cfgFin);
                    if (cfgFin.mesInicioCuotaActual) {
                        setMesCambioCuota(Number(cfgFin.mesInicioCuotaActual));
                    }
                    if (cfgFin.anioInicioCuotaActual) {
                        setAnioCambioCuota(Number(cfgFin.anioInicioCuotaActual));
                    }
                }
            } catch (err) {
                console.error("Error cargando datos de regularización:", err);
                showNotification('Error obteniendo listado de datos', 'error');
            }
        };
        fetchData();
    }, [fraccionamientoId]);

    const handleGuardarVigenciaCuota = async () => {
        if (!fraccionamientoId) return;

        const cuotaNum = parseFloat(vigenciaForm.cuota);
        if (isNaN(cuotaNum) || cuotaNum <= 0) {
            showNotification('Por favor ingresa un monto válido para la cuota mensual ($).', 'warning');
            return;
        }

        const mInicio = Number(vigenciaForm.mesInicio || 1);
        const aInicio = parseInt(vigenciaForm.anioInicio, 10);
        const mFin = Number(vigenciaForm.mesFin || 12);
        const aFin = parseInt(vigenciaForm.anioFin, 10);

        if (!aInicio || !aFin || aInicio < 2000 || aFin < 2000) {
            showNotification('Por favor especifica años válidos de 4 dígitos (ej. 2017).', 'warning');
            return;
        }

        const scoreInicio = aInicio * 12 + mInicio;
        const scoreFin = aFin * 12 + mFin;

        if (scoreInicio > scoreFin) {
            showNotification("El periodo 'Desde mes y año' no puede ser posterior a 'Hasta mes y año'.", 'error');
            return;
        }

        // Detección de traslape con algún registro existente
        const existing = regHistoricoList.find(item => {
            const itemScoreInicio = (item.anioInicio || item.añoInicio) * 12 + (item.mesInicio || 1);
            const itemScoreFin = (item.anioFin || item.añoFin) * 12 + (item.mesFin || 12);
            return Math.max(scoreInicio, itemScoreInicio) <= Math.min(scoreFin, itemScoreFin);
        });

        if (existing) {
            const periodoExistenteStr = formatPeriodoHistorico(existing);
            const nuevoPeriodoStr = `${getNombreMes(mInicio)} ${aInicio} - ${getNombreMes(mFin)} ${aFin}`;
            const confirmMsg = `El periodo ${nuevoPeriodoStr} se traslapa con el registro existente:\n\n• ${periodoExistenteStr} ($${(existing.cuota || 0).toFixed(2)})\n\n¿Deseas reemplazar el registro anterior con esta nueva vigencia de cuota?`;
            if (!window.confirm(confirmMsg)) {
                return;
            }
            await doDeleteHistoricoNoConfirm(existing.id);
        }

        setGuardandoVigencia(true);
        try {
            const histRef = collection(db, 'fraccionamientos', fraccionamientoId, 'historicoCuotas');
            const docRef = await addDoc(histRef, {
                mesInicio: mInicio,
                anioInicio: aInicio,
                mesFin: mFin,
                anioFin: aFin,
                añoInicio: aInicio, // retrocompatibilidad
                añoFin: aFin,       // retrocompatibilidad
                cuota: cuotaNum,
                fechaCreacion: new Date()
            });

            const nuevoItem = {
                id: docRef.id,
                mesInicio: mInicio,
                anioInicio: aInicio,
                mesFin: mFin,
                anioFin: aFin,
                añoInicio: aInicio,
                añoFin: aFin,
                cuota: cuotaNum
            };

            setRegHistoricoList(prev => [
                nuevoItem,
                ...prev.filter(item => item.id !== (existing ? existing.id : null))
            ].sort((a, b) => {
                const scoreA = (b.anioInicio || b.añoInicio || 0) * 12 + (b.mesInicio || 1);
                const scoreB = (a.anioInicio || a.añoInicio || 0) * 12 + (a.mesInicio || 1);
                return scoreA - scoreB;
            }));

            // Actualizar referencia de vigencia
            setMesCambioCuota(mInicio);
            setAnioCambioCuota(aInicio);

            // Limpiar cuota para la siguiente captura manteniendo contexto de fechas
            setVigenciaForm(prev => ({
                ...prev,
                cuota: ''
            }));

            showNotification('Vigencia y cuota de mantenimiento guardada con éxito.', 'success');
        } catch (err) {
            console.error('Error guardando vigencia de cuota:', err);
            showNotification('Error al guardar la vigencia de la cuota.', 'error');
        } finally {
            setGuardandoVigencia(false);
        }
    };

    const doDeleteHistoricoNoConfirm = async (id) => {
        try {
            const docRef = doc(db, 'fraccionamientos', fraccionamientoId, 'historicoCuotas', id);
            await deleteDoc(docRef);
            setRegHistoricoList(prev => prev.filter(item => item.id !== id));
        } catch (error) {
            console.error("Error al eliminar cuota histórica sin confirmación:", error);
        }
    };

    const handleDeleteHistorico = async (id) => {
        if (!window.confirm("¿Seguro que deseas eliminar este período de cuota histórica?")) return;
        try {
            const docRef = doc(db, 'fraccionamientos', fraccionamientoId, 'historicoCuotas', id);
            await deleteDoc(docRef);
            setRegHistoricoList(prev => prev.filter(item => item.id !== id));
            showNotification('Período histórico eliminado.', 'success');
        } catch (error) {
            console.error("Error al eliminar cuota histórica:", error);
            showNotification('Error al eliminar cuota histórica.', 'error');
        }
    };

    const sanearCasaCasetaLocal = async (casa) => {
        if (!casa || !casa.id) return { success: false, error: 'Casa no válida' };
        
        try {
            // 1. Eliminar todos los mesesPendientes registrados para la caseta
            const mesesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas', casa.id, 'mesesPendientes');
            const mesesSnap = await getDocs(mesesRef);
            let eliminados = 0;
            for (const docMes of mesesSnap.docs) {
                await deleteDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'casas', casa.id, 'mesesPendientes', docMes.id));
                eliminados++;
            }

            // 2. Resetear valores contables a 0 y fijar rol caseta
            const casaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', casa.id);
            await updateDoc(casaRef, {
                adeudo: 0,
                adeudoCapital: 0,
                adeudoIntereses: 0,
                adeudoInteres: 0,
                adeudoAgua: 0,
                adeudoMultas: 0,
                sancionesAdmin: 0,
                cuotasExtras: 0,
                saldoFavor: 0,
                mesesAdeudo: 0,
                moroso: false,
                esRolCaseta: true,
                rol: 'caseta',
                rolAsignado: 'caseta',
                perfil: 'caseta',
                ultimaActualizacionSaldo: new Date()
            });

            // 3. Actualizar estado local de casas
            setCasas(prevCasas => prevCasas.map(c => {
                if (c.id === casa.id) {
                    return {
                        ...c,
                        adeudo: 0,
                        adeudoCapital: 0,
                        adeudoIntereses: 0,
                        adeudoInteres: 0,
                        adeudoAgua: 0,
                        adeudoMultas: 0,
                        sancionesAdmin: 0,
                        cuotasExtras: 0,
                        saldoFavor: 0,
                        mesesAdeudo: 0,
                        moroso: false,
                        esRolCaseta: true,
                        rol: 'caseta',
                        rolAsignado: 'caseta',
                        perfil: 'caseta'
                    };
                }
                return c;
            }));

            return { success: true, eliminados };
        } catch (error) {
            console.error(`Error al sanear vivienda de caseta ${casa.numero}:`, error);
            return { success: false, error: error.message };
        }
    };

    const handleSanearCaseta = async (casaDirecta = null) => {
        let casaTarget = casaDirecta;
        
        if (!casaTarget && casaBusqueda.trim()) {
            casaTarget = casas.find(c => String(c.numero).toLowerCase() === String(casaBusqueda).trim().toLowerCase());
        }

        if (!casaTarget) {
            casaTarget = casas.find(c => esCasaCaseta(c));
        }

        if (!casaTarget) {
            showNotification('No se encontró ninguna vivienda de caseta o seguridad en el fraccionamiento.', 'warning');
            return;
        }

        if (!casaDirecta) {
            const confirmMsg = `🛡️ SANEAMIENTO DE CASETA / SEGURIDAD\n\nVivienda detectada: ${casaTarget.numero}\n\nEsta acción:\n1. Dejará su adeudo en $0.00.\n2. Limpiará su historial de meses pendientes o cuotas generadas por error.\n3. Asegurará que quede permanentemente excluida de cobros y recargos.\n\n¿Deseas continuar con el saneamiento?`;
            if (!window.confirm(confirmMsg)) {
                return;
            }
        }

        setProcesandoIndividual(true);
        try {
            const res = await sanearCasaCasetaLocal(casaTarget);
            if (res.success) {
                showNotification(`Casa ${casaTarget.numero} saneada con éxito ($0.00 de adeudo, ${res.eliminados} meses limpiados).`, 'success');
                setResultados(prev => ({
                    ...prev,
                    detalles: [{
                        casaId: casaTarget.id,
                        casaNumero: casaTarget.numero,
                        success: true,
                        skipped: true,
                        reason: `Caseta saneada a $0.00 (${res.eliminados} registros eliminados)`
                    }, ...prev.detalles]
                }));
                setShowDetails(true);
                setCasaBusqueda('');
            } else {
                showNotification(`Error saneando caseta: ${res.error}`, 'error');
            }
        } catch (err) {
            showNotification(`Error: ${err.message}`, 'error');
        } finally {
            setProcesandoIndividual(false);
        }
    };

    const procesarCasaIndividual = async () => {
        if (!casaBusqueda.trim()) {
            showNotification('Por favor, ingresa el número de la casa.', 'warning');
            return;
        }

        // Buscar casa en la lista cargada (por el campo 'numero')
        const casaEncontrada = casas.find(c => String(c.numero).toLowerCase() === String(casaBusqueda).trim().toLowerCase());

        if (!casaEncontrada) {
            showNotification(`La casa número "${casaBusqueda}" no se encontró en este fraccionamiento.`, 'error');
            return;
        }

        if (esCasaCaseta(casaEncontrada)) {
            if (window.confirm(`La casa número "${casaEncontrada.numero}" corresponde a Caseta/Seguridad y está blindada contra regularizaciones financieras ordinarias.\n\n¿Deseas SANEAR esta vivienda ahora (dejar su adeudo en $0.00 y eliminar los registros de meses pendientes residuales)?`)) {
                await handleSanearCaseta(casaEncontrada);
            } else {
                showNotification(`La casa número "${casaEncontrada.numero}" corresponde a Caseta/Seguridad y está blindada contra regularizaciones financieras.`, 'info');
            }
            return;
        }

        const esReversion = opTipo === 'REVERSION';
        const msgConfirm = esReversion
            ? `¿Confirmas la reversión de cargos duplicados de Junio 2026 ÚNICAMENTE para la casa número ${casaEncontrada.numero}?\n\nSe ajustará su saldo a favor o deuda según corresponda.`
            : `¿Confirmas la regularización ÚNICAMENTE para la casa número ${casaEncontrada.numero}?\n\nEsto re-calculará sus meses y su historial.`;

        if (!window.confirm(msgConfirm)) {
            return;
        }

        setProcesandoIndividual(true);
        try {
            const nombreFuncion = esReversion ? 'reversarCargosDuplicadosJunio2026' : 'migrarCasaAMesesPendientes';
            const ejecutarFuncion = httpsCallable(functions, nombreFuncion);
            const payload = esReversion
                ? { fraccionamientoId, casaId: casaEncontrada.id }
                : { 
                    fraccionamientoId, 
                    casaId: casaEncontrada.id,
                    mesInicioCuotaActual: Number(mesCambioCuota),
                    anioInicioCuotaActual: Number(anioCambioCuota)
                  };
            const result = await ejecutarFuncion(payload);
            const data = result.data;

            // Agregar a resultados para que el admin vea el detalle
            setResultados(prev => ({
                ...prev,
                migradas: prev.migradas + (data.success && !data.skipped ? 1 : 0),
                saltadas: prev.saltadas + (data.skipped ? 1 : 0),
                errores: prev.errores + (!data.success ? 1 : 0),
                detalles: [{ casaId: casaEncontrada.id, casaNumero: casaEncontrada.numero, ...data }, ...prev.detalles]
            }));

            if (data.success && !data.skipped) {
                const msgExito = esReversion
                    ? `Casa ${casaEncontrada.numero}: cargos dobles reversados con éxito.`
                    : `Casa ${casaEncontrada.numero} regularizada con éxito. Revisa los detalles abajo.`;
                showNotification(msgExito, 'success');
            } else if (data.skipped) {
                showNotification(`Casa ${casaEncontrada.numero}: ${data.reason}.`, 'info');
            } else {
                showNotification(`Error al procesar la casa ${casaEncontrada.numero}.`, 'error');
            }

            setCasaBusqueda(''); // Limpiar el input si fue exitoso
        } catch (error) {
            console.error(`Error procesando individualmente la casa ${casaEncontrada.numero}:`, error);
            showNotification(`Error: ${error.message}`, 'error');

            setResultados(prev => ({
                ...prev,
                errores: prev.errores + 1,
                detalles: [{ casaId: casaEncontrada.id, casaNumero: casaEncontrada.numero, success: false, error: error.message }, ...prev.detalles]
            }));
        } finally {
            setProcesandoIndividual(false);
            setShowDetails(true); // Abrir detalles para que vean el resultado
        }
    };

    const recalcularCasaIndividual = async () => {
        if (!casaBusqueda.trim()) {
            showNotification('Por favor, ingresa el número de la casa.', 'warning');
            return;
        }

        const casaEncontrada = casas.find(c => String(c.numero).toLowerCase() === String(casaBusqueda).trim().toLowerCase());

        if (!casaEncontrada) {
            showNotification(`La casa número "${casaBusqueda}" no se encontró en este fraccionamiento.`, 'error');
            return;
        }

        if (esCasaCaseta(casaEncontrada)) {
            if (window.confirm(`La casa número "${casaEncontrada.numero}" corresponde a Caseta/Seguridad y está blindada contra recálculos de deudas.\n\n¿Deseas SANEAR esta vivienda ahora (dejar su adeudo en $0.00 y eliminar los registros de meses pendientes residuales)?`)) {
                await handleSanearCaseta(casaEncontrada);
            } else {
                showNotification(`La casa número "${casaEncontrada.numero}" corresponde a Caseta/Seguridad y está blindada contra recálculos de deudas.`, 'info');
            }
            return;
        }

        if (!window.confirm(`¿Confirmas el recálculo contable de deudas ÚNICAMENTE para la casa número ${casaEncontrada.numero}?\n\nSe sumarán los meses de mantenimiento ordinario (formato AAAA-MM) y se reajustará el total.`)) {
            return;
        }

        setProcesandoIndividual(true);
        try {
            const data = await recalcularCasaLocal(casaEncontrada.id, casaEncontrada.numero);

            setResultados(prev => ({
                ...prev,
                migradas: prev.migradas + (data.success && !data.skipped ? 1 : 0),
                saltadas: prev.saltadas + (data.skipped ? 1 : 0),
                errores: prev.errores + (!data.success ? 1 : 0),
                detalles: [{ casaId: casaEncontrada.id, casaNumero: casaEncontrada.numero, ...data }, ...prev.detalles]
            }));

            if (data.success && !data.skipped) {
                showNotification(`Casa ${casaEncontrada.numero} recalculada con éxito. Adeudo: $${(data.adeudo || 0).toFixed(2)}.`, 'success');
                setCasaBusqueda(''); // Limpiar si fue exitoso
            } else if (data.skipped) {
                showNotification(`Casa ${casaEncontrada.numero}: ${data.reason}.`, 'info');
            } else {
                showNotification(`Error al recalcular la casa ${casaEncontrada.numero}.`, 'error');
            }
        } catch (error) {
            console.error(`Error recalculando individualmente la casa ${casaEncontrada.numero}:`, error);
            showNotification(`Error: ${error.message}`, 'error');

            setResultados(prev => ({
                ...prev,
                errores: prev.errores + 1,
                detalles: [{ casaId: casaEncontrada.id, casaNumero: casaEncontrada.numero, success: false, error: error.message }, ...prev.detalles]
            }));
        } finally {
            setProcesandoIndividual(false);
            setShowDetails(true);
        }
    };

    const recalcularCasaLocal = async (casaId, casaNumero) => {
        try {
            const casaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', casaId);
            const casaSnap = await getDoc(casaRef);
            
            if (!casaSnap.exists()) {
                return { success: false, error: 'Casa no encontrada en la base de datos' };
            }
            const casaData = casaSnap.data() || {};
            
            if (esCasaCaseta(casaData)) {
                return { success: true, skipped: true, reason: 'Vivienda de Caseta/Seguridad (Blindada)' };
            }

            // 1. Cargar mesesPendientes
            const mesesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas', casaId, 'mesesPendientes');
            const mesesSnap = await getDocs(mesesRef);
            const mesesList = mesesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            // Filtrar meses de mantenimiento ordinario (ID formato AAAA-MM o prefijo his-)
            const mesesMantenimiento = mesesList.filter(m => /^\d{4}-\d{2}$/.test(m.id) || m.id.startsWith('his-'));

            // Freno de seguridad: si no hay documentos de meses, saltamos para evitar errores financieros
            if (mesesMantenimiento.length === 0) {
                return { success: true, skipped: true, reason: 'Omitida: requiere regularización integral previa' };
            }

            // 2. Sumar capital ordinario de mantenimiento
            const totalMantenimientoCapital = mesesMantenimiento.reduce((sum, m) => {
                const pendiente = m.montoPendiente !== undefined 
                    ? Number(m.montoPendiente) 
                    : (Number(m.monto || m.cuotaMensual || 0) - Number(m.montoPagado || 0));
                return sum + Math.max(0, pendiente);
            }, 0);

            // Contar meses de adeudo activos
            const numMeses = mesesMantenimiento.filter(m => {
                const pendiente = m.montoPendiente !== undefined 
                    ? Number(m.montoPendiente) 
                    : (Number(m.monto || m.cuotaMensual || 0) - Number(m.montoPagado || 0));
                return Math.max(0, pendiente) > 0;
            }).length;

            // 3. Cargar multas administrativas no pagadas
            const multasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas');
            const qMultas = query(multasRef, where('casaId', '==', casaId));
            const multasSnap = await getDocs(qMultas);
            const totalMultas = multasSnap.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(m => !m.pagada)
                .reduce((sum, m) => sum + Number(m.monto || 0), 0);

            const totalMantenimientoIntereses = Number(casaData.adeudoIntereses ?? casaData.adeudoInteres ?? 0);
            const nuevoAdeudoCapital = totalMantenimientoCapital;

            const totalAgua = mesesList
                .filter(m => m.tipo === 'adeudo_agua' && (m.estado === 'pendiente' || m.estado === 'moroso'))
                .reduce((sum, m) => sum + Number(m.montoPendiente ?? m.monto ?? 0), 0);
            const totalExtras = mesesList
                .filter(m => (m.id?.startsWith('EXT-') || m.tipo === 'cuota_extraordinaria') && m.estado !== 'pagado_completo' && m.estado !== 'pagado_totalmente')
                .reduce((sum, m) => sum + Number(m.montoPendiente ?? m.monto ?? 0), 0);
            const saldoFavor = Number(casaData.saldoFavor || 0);

            // Adeudo Total (adeudo) = Capital + Intereses + Agua + Extras + Multas - SaldoFavor
            const nuevoAdeudoTotal = Math.max(0, nuevoAdeudoCapital + totalMantenimientoIntereses + totalAgua + totalExtras + totalMultas - saldoFavor);

            // 4. Actualizar en Firestore
            await updateDoc(casaRef, {
                adeudo: nuevoAdeudoTotal,
                adeudoCapital: nuevoAdeudoCapital,
                adeudoIntereses: totalMantenimientoIntereses,
                adeudoInteres: totalMantenimientoIntereses,
                cuotasExtras: totalExtras,
                mesesAdeudo: numMeses,
                ultimaActualizacionSaldo: new Date()
            });

            return {
                success: true,
                skipped: false,
                adeudo: nuevoAdeudoTotal,
                adeudoCapital: nuevoAdeudoCapital,
                adeudoIntereses: totalMantenimientoIntereses,
                mesesActualizados: numMeses
            };
        } catch (error) {
            console.error(`Error al recalcular deudas de la casa ${casaNumero}:`, error);
            return { success: false, error: error.message };
        }
    };

    const procesarLote = async () => {
        if (!fraccionamientoId || casas.length === 0) return;

        const esReversion = opTipo === 'REVERSION';
        const esRecalculo = subOp === 'RECALCULO' && opTipo === 'HISTORICO';
        let mensajeConfirmacion = '';

        if (esReversion) {
            mensajeConfirmacion = `¿Confirmas la reversión masiva de cargos duplicados de Junio 2026?\n\n` +
                `• Se procesarán en bloques de ${BLOQUE_SIZE} con ${PAUSA_MS / 1000}s de pausa.\n` +
                `• Se recalcularán los saldos a favor y adeudos de los residentes duplicados.\n` +
                `• Se eliminarán los recibos automáticos de junio tipo extraordinario.\n\n` +
                `¿Deseas iniciar el proceso masivo de reversión?`;
        } else if (esRecalculo) {
            mensajeConfirmacion = `¿Confirmas el recálculo masivo contable de deudas para todo el fraccionamiento?\n\n` +
                `• Se procesarán en bloques de ${BLOQUE_SIZE} con ${PAUSA_MS / 1000}s de pausa.\n` +
                `• Se sumarán los saldos pendientes de meses con formato AAAA-MM.\n` +
                `• Se omitirán casas que no cuenten con meses migrados previamente.\n\n` +
                `¿Deseas iniciar el proceso de recálculo masivo?`;
        } else {
            mensajeConfirmacion = `¿Confirmas la regularización financiera integral?\n\n` +
                `• Se procesarán en bloques de ${BLOQUE_SIZE} con ${PAUSA_MS / 1000}s de pausa.\n` +
                `• Para el año actual (${añoActual}) se generarán meses individuales.\n` +
                `• Para años anteriores, se aplicará el HISTÓRICO DE CUOTAS configurado.\n` +
                `• Si no hay histórico configurado, se usará el monto actual como fallback.\n\n` +
                `¿Deseas iniciar el proceso masivo?`;
        }

        if (fase === 'INICIO' && !window.confirm(mensajeConfirmacion)) {
            return;
        }

        setFase('PROCESANDO');
        setStopRequested(false);
        setShowDetails(true); // Mostrar detalles automáticamente al iniciar

        const nombreFuncion = esReversion ? 'reversarCargosDuplicadosJunio2026' : 'migrarCasaAMesesPendientes';
        const ejecutarFuncion = httpsCallable(functions, nombreFuncion);
        let index = indiceActual;
        const limiteLote = Math.min(index + BLOQUE_SIZE, casas.length);

        for (; index < limiteLote; index++) {
            // Verificar si el usuario solicitó detener el proceso
            if (stopRequested) {
                break;
            }

            const casa = casas[index];

            // Blindaje de Caseta: Omitir de lote
            if (esCasaCaseta(casa)) {
                setResultados(prev => ({
                    ...prev,
                    saltadas: prev.saltadas + 1,
                    detalles: [{ casaId: casa.id, casaNumero: casa.numero, success: true, skipped: true, reason: 'Caseta/Seguridad (Blindada)' }, ...prev.detalles]
                }));
                setIndiceActual(index + 1);
                continue;
            }

            try {
                let data;
                if (esRecalculo) {
                    // Ejecutar el motor de recálculo local del frontend directo en Firestore
                    data = await recalcularCasaLocal(casa.id, casa.numero);
                } else {
                    // Ejecutar función Cloud del backend
                    const payload = esReversion
                        ? { fraccionamientoId, casaId: casa.id }
                        : { 
                            fraccionamientoId, 
                            casaId: casa.id,
                            mesInicioCuotaActual: Number(mesCambioCuota),
                            anioInicioCuotaActual: Number(anioCambioCuota)
                          };
                    const result = await ejecutarFuncion(payload);
                    data = result.data;
                }

                setResultados(prev => ({
                    ...prev,
                    migradas: prev.migradas + (data.success && !data.skipped ? 1 : 0),
                    saltadas: prev.saltadas + (data.skipped ? 1 : 0),
                    errores: prev.errores + (!data.success ? 1 : 0),
                    // Prepend para que la casa más reciente aparezca arriba
                    detalles: [{ casaId: casa.id, casaNumero: casa.numero, ...data }, ...prev.detalles]
                }));
            } catch (error) {
                console.error(`Error en casa ${casa.numero}:`, error);
                setResultados(prev => ({
                    ...prev,
                    errores: prev.errores + 1,
                    detalles: [{ casaId: casa.id, casaNumero: casa.numero, success: false, error: error.message }, ...prev.detalles]
                }));
            }

            setIndiceActual(index + 1);

            // Pausa exigida (excepto en la última del total o si se solicitó parar)
            if (index + 1 < casas.length && !stopRequested) {
                await new Promise(resolve => setTimeout(resolve, PAUSA_MS));
            }
        }

        if (stopRequested) {
            setFase('PAUSADO_LOTE');
            setStopRequested(false);
            showNotification('Proceso detenido por el usuario.', 'info');
            return;
        }

        if (index >= casas.length) {
            setFase('COMPLETADO');
            const msgCompletado = esReversion
                ? 'Proceso de reversión de cargos completado en su totalidad.'
                : 'Proceso de regularización completado en su totalidad.';
            showNotification(msgCompletado, 'success');
        } else {
            setFase('PAUSADO_LOTE');
            showNotification(`Lote de ${BLOQUE_SIZE} completado. Por favor, continúa con el siguiente grupo.`, 'info');
        }
    };

    // Helper para mostrar los estados del mes de forma amigable
    const renderEstadosMes = (estadosPorMes) => {
        if (!estadosPorMes) return null;
        
        // Usar los meses calculados dinámicamente o los que vengan en el resultado
        const mesesAMostrar = Object.keys(estadosPorMes).length > 0 
            ? Object.keys(estadosPorMes).sort() 
            : mesesARegularizar;

        const etiquetas = {
            'pagado_totalmente': { texto: '✓', clase: 'estado-pagado', full: 'Pagado' },
            'pendiente': { texto: '✗', clase: 'estado-pendiente', full: 'Pendiente' },
            'pagado_parcialmente': { texto: '!', clase: 'estado-parcial', full: 'Parcial' }
        };

        return (
            <div className="estados-meses-grid">
                {mesesAMostrar.map(mes => {
                    const estado = estadosPorMes[mes];
                    const info = etiquetas[estado] || { texto: '?', clase: '', full: estado };
                    
                    let nombre = nombresMeses[mes];
                    if (!nombre) {
                        const [mAnio, mMes] = mes.split('-');
                        const d = new Date(parseInt(mAnio), parseInt(mMes) - 1);
                        nombre = new Intl.DateTimeFormat('es-MX', { month: 'short' }).format(d);
                        nombre = nombre.charAt(0).toUpperCase() + nombre.slice(1).replace('.', '');
                    }

                    return (
                        <div key={mes} className={`mes-block ${info.clase}`} title={`${nombre}: ${info.full}`}>
                            <span className="mes-name-mini">{nombre}</span>
                            <span className="mes-status-icon">{info.texto}</span>
                        </div>
                    );
                })}
            </div>
        );
    };



    return (
        <div className="migracion-meses-container">
            {/* Sub-pestañas con diseño moderno pill-tabs */}
            <div className="migracion-tabs">
                <button
                    className={`tab-btn ${opTipo === 'HISTORICO' ? 'active tab-historico' : ''}`}
                    onClick={() => cambiarPestana('HISTORICO')}
                    disabled={fase === 'PROCESANDO'}
                >
                    📅 Crear Meses Históricos
                </button>
                <button
                    className={`tab-btn ${opTipo === 'REVERSION' ? 'active tab-reversion' : ''}`}
                    onClick={() => cambiarPestana('REVERSION')}
                    disabled={fase === 'PROCESANDO'}
                >
                    🔄 Revertir Cargos Duplicados (Junio 2026)
                </button>
            </div>

            {opTipo === 'HISTORICO' ? (
                <>
                    <div className="migracion-header">
                        <h3>🔄 Regularización Financiera Integral</h3>
                        <p className="migracion-description">
                            Este motor recalcula adeudos de forma inteligente, aplicando el <strong>Histórico de Cuotas</strong> configurado para años anteriores y generando meses detallados para el año en curso ({añoActual}).
                        </p>
                    </div>

                    <div className="reg-historic-section-global" style={{ marginBottom: '20px' }}>
                        <div className="reg-historic-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>📜 Histórico y Vigencia de Cuotas de Mantenimiento</h4>
                            {regSubdivisionConfig?.montoMensual && (
                                <span className="current-fee-badge" style={{ fontSize: '0.9rem', backgroundColor: '#e0f2fe', color: '#0369a1', padding: '6px 12px', borderRadius: '20px', fontWeight: '600' }}>
                                    Cuota Actual Fraccionamiento: <strong>${parseFloat(regSubdivisionConfig.montoMensual).toFixed(2)}</strong>
                                </span>
                            )}
                        </div>

                        <div className="reg-historic-grid">
                            {/* Panel Izquierdo: Formulario Unificado de Vigencia (según diseño Imagen 2) */}
                            <div className="reg-vigencia-unified-card">
                                <div className="vigencia-form-table">
                                    {/* Fila 1: Cuota por mes $ */}
                                    <div className="vigencia-table-row fee-header-row">
                                        <div className="vigencia-cell-label highlight">
                                            <span>Cuota por mes $</span>
                                        </div>
                                        <div className="vigencia-cell-input fee-input-container">
                                            <span className="currency-symbol">$</span>
                                            <input
                                                type="number"
                                                min="0"
                                                step="any"
                                                placeholder="Ej. 500"
                                                value={vigenciaForm.cuota}
                                                onChange={(e) => setVigenciaForm(prev => ({ ...prev, cuota: e.target.value }))}
                                                className="input-cuota-vigencia"
                                            />
                                        </div>
                                    </div>

                                    {/* Fila 2: Desde mes y Año */}
                                    <div className="vigencia-table-row range-row">
                                        <div className="range-subfield month-subfield">
                                            <span className="vigencia-sublabel">Desde mes</span>
                                            <select
                                                value={vigenciaForm.mesInicio}
                                                onChange={(e) => setVigenciaForm(prev => ({ ...prev, mesInicio: Number(e.target.value) }))}
                                                className="select-vigencia"
                                            >
                                                {MESES_OPCIONES.map(m => (
                                                    <option key={`desde-${m.value}`} value={m.value}>{m.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="range-subfield year-subfield">
                                            <span className="vigencia-sublabel">Año</span>
                                            <input
                                                type="number"
                                                placeholder="2017"
                                                value={vigenciaForm.anioInicio}
                                                onChange={(e) => setVigenciaForm(prev => ({ ...prev, anioInicio: e.target.value }))}
                                                className="input-vigencia-year"
                                            />
                                        </div>
                                    </div>

                                    {/* Fila 3: Hasta mes y Año */}
                                    <div className="vigencia-table-row range-row">
                                        <div className="range-subfield month-subfield">
                                            <span className="vigencia-sublabel">Hasta mes</span>
                                            <select
                                                value={vigenciaForm.mesFin}
                                                onChange={(e) => setVigenciaForm(prev => ({ ...prev, mesFin: Number(e.target.value) }))}
                                                className="select-vigencia"
                                            >
                                                {MESES_OPCIONES.map(m => (
                                                    <option key={`hasta-${m.value}`} value={m.value}>{m.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="range-subfield year-subfield">
                                            <span className="vigencia-sublabel">Año</span>
                                            <input
                                                type="number"
                                                placeholder="2018"
                                                value={vigenciaForm.anioFin}
                                                onChange={(e) => setVigenciaForm(prev => ({ ...prev, anioFin: e.target.value }))}
                                                className="input-vigencia-year"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="vigencia-submit-row">
                                    <button
                                        type="button"
                                        onClick={handleGuardarVigenciaCuota}
                                        disabled={guardandoVigencia || !vigenciaForm.cuota}
                                        className="btn-guardar-vigencia-action"
                                        title="Guardar vigencia y agregar a cuotas del fraccionamiento"
                                    >
                                        {guardandoVigencia ? 'Guardando...' : '💾 Guardar Vigencia'}
                                    </button>
                                </div>
                            </div>

                            {/* Panel Derecho: Campos Guardados con Meses y Años */}
                            <div className="reg-historic-right">
                                <div className="campos-guardados-header">
                                    <h5 style={{ margin: 0, fontSize: '0.92rem', color: '#1e293b', fontWeight: '700' }}>Campos Guardados</h5>
                                    <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>
                                        {regHistoricoList.length} {regHistoricoList.length === 1 ? 'periodo' : 'periodos'}
                                    </span>
                                </div>
                                <div className="hist-mini-table-container">
                                    <table className="hist-mini-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid #cbd5e1', textAlign: 'left', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase' }}>
                                                <th style={{ padding: '8px 6px' }}>PERIODO</th>
                                                <th style={{ padding: '8px 6px' }}>CUOTA</th>
                                                <th style={{ padding: '8px 6px', width: '30px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {regHistoricoList.length === 0 ? (
                                                <tr>
                                                    <td colSpan="3" style={{ textAlign: 'center', color: '#94a3b8', padding: '16px', fontSize: '0.85rem' }}>
                                                        Sin periodos de cuota registrados
                                                    </td>
                                                </tr>
                                            ) : (
                                                regHistoricoList.map(item => (
                                                    <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '0.86rem' }}>
                                                        <td style={{ padding: '8px 6px', color: '#1e293b' }}>
                                                            <strong>{formatPeriodoHistorico(item)}</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 6px', color: '#0369a1', fontWeight: '700' }}>
                                                            ${(item.cuota || 0).toFixed(2)}
                                                        </td>
                                                        <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                                                            <button 
                                                                type="button" 
                                                                className="del-hist-btn"
                                                                onClick={() => handleDeleteHistorico(item.id)}
                                                                title="Eliminar este periodo"
                                                                style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px', fontSize: '0.85rem' }}
                                                            >
                                                                <FaTimes />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="migracion-warning" style={{ borderLeftColor: '#3b82f6', background: '#eff6ff' }}>
                        <strong>🚀 Motor de Cálculo Dinámico Activo:</strong>
                        <ul style={{ color: '#1e40af' }}>
                            <li>Aplica cuotas retroactivas según la configuración de tu fraccionamiento.</li>
                            <li>Sistema de Paginación: <strong>6 segundos de pausa</strong> entre casas para evitar saturación.</li>
                            <li>Control por Bloques: Se pausará cada <strong>{BLOQUE_SIZE} casas</strong> para tu revisión.</li>
                        </ul>
                    </div>
                </>
            ) : (
                <>
                    <div className="migracion-header header-reversion">
                        <h3>🛡️ Reversión de Cargos Duplicados - Junio 2026</h3>
                        <p className="migracion-description">
                            Herramienta de regularización inversa para corregir los cargos dobles del 1 de Junio de 2026. Revierte la cuota extraordinaria cobrada en el mes 2026-06 y reajusta saldos.
                        </p>
                    </div>

                    <div className="migracion-warning warning-reversion" style={{ borderLeftColor: '#10b981', background: '#ecfdf5' }}>
                        <strong>✨ Algoritmo de Reversión Inteligente Activo:</strong>
                        <ul style={{ color: '#065f46' }}>
                            <li><strong>Caso A (Saldo a Favor):</strong> Reembolsa el cargo extra a Saldo a Favor y actualiza el mes.</li>
                            <li><strong>Caso B (Adeudo):</strong> Reduce el adeudo global de la vivienda y actualiza el mes.</li>
                            <li><strong>Caso C (Pago Parcial):</strong> Calcula el remanente proporcional, reembolsa a favor si excede el nuevo total, o ajusta la deuda pendiente.</li>
                            <li><strong>Eliminación de Ingresos:</strong> Localiza y elimina los ingresos duplicados del corte erróneo.</li>
                            <li>Paginación: <strong>6 segundos de pausa</strong> entre casas (bloques de <strong>{BLOQUE_SIZE}</strong>).</li>
                        </ul>
                    </div>
                </>
            )}

            {/* Nueva Sección: Búsqueda Individual */}
            <div className={`migracion-individual-container ${opTipo === 'REVERSION' ? 'individual-reversion' : ''}`}>
                <h4>{opTipo === 'REVERSION' ? '🔍 Revertir Casa Específica' : '🔍 Regularizar Casa Específica'}</h4>
                <p>
                    {opTipo === 'REVERSION'
                        ? 'Ejecuta la corrección únicamente para una vivienda seleccionada.'
                        : 'Usa esto para regularizar una sola casa (ideal tras borrar un recibo antiguo). No afectará el proceso por lotes.'}
                </p>
                {casetasConSaldo.length > 0 && (
                    <div style={{ backgroundColor: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                        <div style={{ color: '#b45309', fontSize: '0.9rem' }}>
                            <strong>⚠️ Vivienda de Caseta con saldo residual detectada:</strong> {casetasConSaldo.map(c => `Casa ${c.numero} ($${(c.adeudo || 0).toLocaleString('es-MX')})`).join(', ')}. Las casetas no deben registrar adeudos ni cuotas.
                        </div>
                        <button
                            type="button"
                            onClick={() => handleSanearCaseta(casetasConSaldo[0])}
                            disabled={procesandoIndividual || fase === 'PROCESANDO'}
                            style={{ backgroundColor: '#d97706', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '8px 14px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem' }}
                        >
                            🛡️ Sanear a $0 Ahora
                        </button>
                    </div>
                )}
                <div className="migracion-individual-form" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <input
                        type="text"
                        placeholder="Ej: 333"
                        value={casaBusqueda}
                        onChange={(e) => setCasaBusqueda(e.target.value)}
                        disabled={procesandoIndividual || fase === 'PROCESANDO'}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') procesarCasaIndividual();
                        }}
                    />
                    <button
                        className={`btn-migrar-individual ${opTipo === 'REVERSION' ? 'btn-revertir-individual' : ''}`}
                        onClick={procesarCasaIndividual}
                        disabled={procesandoIndividual || casas.length === 0 || fase === 'PROCESANDO'}
                    >
                        {procesandoIndividual ? 'Procesando...' : opTipo === 'REVERSION' ? '🔄 Revertir Individual' : '🔍 Regularizar Individual'}
                    </button>
                    {opTipo === 'HISTORICO' && (
                        <button
                            className="btn-recalcular-individual"
                            onClick={recalcularCasaIndividual}
                            disabled={procesandoIndividual || casas.length === 0 || fase === 'PROCESANDO'}
                            style={{ 
                                padding: '12px 24px', 
                                backgroundColor: '#10b981', 
                                color: '#ffffff', 
                                border: 'none', 
                                borderRadius: '6px', 
                                fontSize: '1rem', 
                                fontWeight: 'bold', 
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {procesandoIndividual ? 'Procesando...' : '🧮 Recalcular Individual'}
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn-sanear-caseta-individual"
                        onClick={() => handleSanearCaseta()}
                        disabled={procesandoIndividual || casas.length === 0 || fase === 'PROCESANDO'}
                        style={{ 
                            padding: '12px 20px', 
                            backgroundColor: '#0f172a', 
                            color: '#38bdf8', 
                            border: '1px solid #38bdf8', 
                            borderRadius: '6px', 
                            fontSize: '0.95rem', 
                            fontWeight: 'bold', 
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap'
                        }}
                        title="Sanear vivienda de Caseta/Seguridad a $0.00 y limpiar sus meses pendientes"
                    >
                        {procesandoIndividual ? 'Procesando...' : '🛡️ Sanear Caseta ($0)'}
                    </button>
                </div>
            </div>

            <div style={{ textAlign: 'center', margin: '20px 0' }}>
                <p style={{ fontSize: '1.1rem', marginBottom: '16px' }}>
                    <strong>Progreso General:</strong> {indiceActual} de {casas.length} casas ({casas.length > 0 ? Math.round((indiceActual / casas.length) * 100) : 0}%)
                </p>

                {fase === 'PROCESANDO' && (
                    <div className={`processing-banner ${opTipo === 'REVERSION' ? 'processing-reversion' : ''}`}>
                        <div className="processing-info">
                            {subOp === 'RECALCULO' && opTipo === 'HISTORICO' ? '🧮 Recalculando casa' : '⏳ Procesando casa'} {indiceActual + 1} de {casas.length}...
                            <p className="no-close-warning">(No cierres la página para evitar inconsistencias)</p>
                        </div>
                        <button 
                            className="btn-stop" 
                            onClick={() => setStopRequested(true)}
                            disabled={stopRequested}
                        >
                            {stopRequested ? '⏹️ Deteniendo...' : '🛑 DETENER PROCESO'}
                        </button>
                    </div>
                )}

                {(fase === 'INICIO' || fase === 'PAUSADO_LOTE') && (
                    <div className="action-buttons-group" style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {opTipo === 'REVERSION' ? (
                            <button
                                className="btn-migrar btn-reversar"
                                onClick={procesarLote}
                                disabled={casas.length === 0}
                            >
                                {fase === 'INICIO' ? '🔄 Empezar Reversión Masiva' : '▶️ Continuar Reversión'}
                            </button>
                        ) : (
                            <>
                                {(fase === 'INICIO' || subOp === 'MIGRACION') && (
                                    <button
                                        className="btn-migrar"
                                        onClick={() => {
                                            setSubOp('MIGRACION');
                                            setTimeout(procesarLote, 50);
                                        }}
                                        disabled={casas.length === 0 || (fase === 'PAUSADO_LOTE' && subOp !== 'MIGRACION')}
                                        style={{ padding: '12px 24px', backgroundColor: '#3b82f6', color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer' }}
                                    >
                                        {fase === 'INICIO' ? '🚀 Empezar Regularización Integral' : '▶️ Continuar Regularización'}
                                    </button>
                                )}
                                {(fase === 'INICIO' || subOp === 'RECALCULO') && (
                                    <button
                                        className="btn-recalcular-masivo"
                                        onClick={() => {
                                            setSubOp('RECALCULO');
                                            setTimeout(procesarLote, 50);
                                        }}
                                        disabled={casas.length === 0 || (fase === 'PAUSADO_LOTE' && subOp !== 'RECALCULO')}
                                        style={{ padding: '12px 24px', backgroundColor: '#10b981', color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer' }}
                                    >
                                        {fase === 'INICIO' ? '🧮 Empezar Recálculo Masivo' : '▶️ Continuar Recálculo'}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                )}

                {fase === 'COMPLETADO' && (
                    <div style={{
                        background: opTipo === 'REVERSION' ? '#ecfdf5' : '#d1fae5',
                        color: opTipo === 'REVERSION' ? '#047857' : '#065f46',
                        padding: '12px',
                        borderRadius: '8px',
                        fontWeight: 'bold',
                        border: `1px solid ${opTipo === 'REVERSION' ? '#a7f3d0' : '#86efac'}`
                    }}>
                        ✅ ¡Proceso 100% completado!
                    </div>
                )}
            </div>

            {resultados.detalles.length > 0 && (
                <div className="migracion-result">
                    <h4>Resultados (Acumulado)</h4>
                    <div className="result-summary">
                        <div className="result-item">
                            <span className="result-label">Procesadas:</span>
                            <span className="result-value">{indiceActual}</span>
                        </div>
                        <div className={`result-item success ${opTipo === 'REVERSION' ? 'res-reversion-success' : ''}`}>
                            <span className="result-label">{opTipo === 'REVERSION' ? '✅ Corregidas:' : '✅ Actualizadas:'}</span>
                            <span className="result-value">{resultados.migradas}</span>
                        </div>
                        <div className="result-item info">
                            <span className="result-label">⏭️ Sin cambios:</span>
                            <span className="result-value">{resultados.saltadas}</span>
                        </div>
                        {resultados.errores > 0 && (
                            <div className="result-item error">
                                <span className="result-label">❌ Errores:</span>
                                <span className="result-value">{resultados.errores}</span>
                            </div>
                        )}
                    </div>

                    <div className="result-details">
                        <button
                            className="btn-toggle-details"
                            onClick={() => setShowDetails(!showDetails)}
                        >
                            {showDetails ? '▼ Ocultar Detalles' : opTipo === 'REVERSION' ? '▶ Ver Detalles de la Reversión' : '▶ Ver Detalles Generados'}
                        </button>

                        {showDetails && (
                            <div className="details-list">
                                {resultados.detalles.map((detalle, index) => (
                                    <div
                                        key={index}
                                        className={`detail-item ${detalle.success ? (detalle.skipped ? 'skipped' : 'success') : 'error'} ${opTipo === 'REVERSION' && detalle.success && !detalle.skipped ? 'reversion-item' : ''}`}
                                    >
                                        <div className="detail-header">
                                            <strong>Casa {detalle.casaNumero}</strong>
                                            {detalle.success && !detalle.skipped && (
                                                <span className="badge success">
                                                    {opTipo === 'REVERSION' ? '✓ Cargos Reversados' : `✓ ${detalle.mesesActualizados} ${detalle.mesesActualizados === 1 ? 'mes regularizado' : 'meses regularizados'}`}
                                                </span>
                                            )}

                                            {detalle.skipped && (
                                                <span className="badge info">⏭ Sin cambios</span>
                                            )}
                                            {!detalle.success && (
                                                <span className="badge error">✗ Error</span>
                                            )}
                                        </div>
                                        <div className="detail-body">
                                            {detalle.success && !detalle.skipped && opTipo === 'HISTORICO' && (
                                                <div className="quick-view-annual">
                                                    <p className="quick-view-title">Vista rápida año actual ({añoActual}):</p>
                                                    {renderEstadosMes(detalle.estadosPorMes)}
                                                </div>
                                            )}
                                            
                                            {detalle.skipped && (
                                                <p className="detail-reason">Estado: {detalle.reason}</p>
                                            )}
                                            {!detalle.success && detalle.error && (
                                                <p className="detail-error">Error: {detalle.error}</p>
                                            )}
                                            
                                            {detalle.success && !detalle.skipped && opTipo === 'HISTORICO' && (
                                                <div className="detail-stats-grid">
                                                    <div className="stat-pill">
                                                        <span className="stat-label">Adeudo utilizado:</span>
                                                        <span className="stat-value">${(detalle.adeudo || 0).toFixed(2)}</span>
                                                    </div>
                                                    <div className="stat-pill">
                                                        <span className="stat-label">Saldo a Favor:</span>
                                                        <span className="stat-value">${(detalle.saldoFavor || 0).toFixed(2)}</span>
                                                    </div>
                                                    {detalle.mesesHistoricos > 0 && (
                                                        <div className="stat-pill highlight">
                                                            <span className="stat-label">📜 Docs históricos:</span>
                                                            <span className="stat-value">{detalle.mesesHistoricos} (his-*)</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {detalle.success && !detalle.skipped && opTipo === 'REVERSION' && (
                                                <div className="reversion-details-grid">
                                                    <div className="reversion-stat-group">
                                                        <div className="rev-pill prev">
                                                            <span className="pill-lbl">Monto Anterior 2026-06:</span>
                                                            <span className="pill-val">${(detalle.old_totalMensual || 0).toFixed(2)}</span>
                                                        </div>
                                                        <div className="rev-pill current">
                                                            <span className="pill-lbl">Monto Corregido:</span>
                                                            <span className="pill-val">${(detalle.new_totalMensual || 0).toFixed(2)}</span>
                                                        </div>
                                                    </div>
                                                    <div className="reversion-action-group">
                                                        {detalle.refund_amount > 0 && (
                                                            <div className="rev-pill refund">
                                                                <span className="pill-lbl">💰 Reembolsado a Favor:</span>
                                                                <span className="pill-val">+${(detalle.refund_amount || 0).toFixed(2)}</span>
                                                            </div>
                                                        )}
                                                        {detalle.debt_reduction > 0 && (
                                                            <div className="rev-pill reduction">
                                                                <span className="pill-lbl">📉 Reducción de Adeudo:</span>
                                                                <span className="pill-val">-${(detalle.debt_reduction || 0).toFixed(2)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="reversion-summary-group">
                                                        <div className="rev-pill info-state">
                                                            <span className="pill-lbl">Nuevo Estado Mes:</span>
                                                            <span className="pill-val text-capitalize">{detalle.new_estado?.replace('_', ' ')}</span>
                                                        </div>
                                                        <div className="rev-pill info-balance">
                                                            <span className="pill-lbl">Adeudo Restante:</span>
                                                            <span className="pill-val">${(detalle.new_adeudo || 0).toFixed(2)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MigracionMesesPendientes;
